import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Spinner, Alert } from 'react-bootstrap';
import {
  useGetCrmTaskCountsQuery,
  useListCrmTasksQuery,
  useGetCrmTaskWorkloadQuery,
  useClaimCrmTaskMutation,
  useSaveCrmTaskQueueMutation,
  useSaveCrmTaskTypeMutation,
} from '../../app/api';
import { useIsMobile } from '../utils';
import { work, useTaskLookups, TaskTable, NewTaskModal, TaskDrawer, errText } from './TaskParts';

// Work -> Tasks. Client service work for CSAs, paraplanners and advisors:
//   Mine       what I own or am in the loop on, by due date
//   Queues     unclaimed work in the product queues I belong to
//   Delegated  what I handed to someone else or a queue
//   Team       open work per person and per queue, across what I can see
//   Settings   queues, their members and the task types (WORK_ADMIN)
// Queues are company-wide: every CSA and paraplanner works together.

const TABS = [
  ['mine', 'Mine', 'person-check'],
  ['queues', 'Queues', 'inbox'],
  ['delegated', 'Delegated', 'send'],
  ['team', 'Team', 'people'],
];

export default function Tasks() {
  const isMobile = useIsMobile();
  const { lookups, isLoading, isError } = useTaskLookups();
  const { data: counts, refetch: refetchCounts } = useGetCrmTaskCountsQuery();
  const [tab, setTab] = useState('mine');
  const [status, setStatus] = useState('open');
  const [category, setCategory] = useState('');
  const [teamOwner, setTeamOwner] = useState('');
  const [teamQueue, setTeamQueue] = useState('');
  const [openId, setOpenId] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [error, setError] = useState(null);
  const [claim] = useClaimCrmTaskMutation();

  const listArgs = tab === 'settings' ? null : {
    view: tab,
    status,
    category,
    owner: tab === 'team' ? teamOwner : '',
    queue_id: tab === 'team' ? teamQueue : '',
  };
  const list = useListCrmTasksQuery(listArgs || {}, { skip: !listArgs });
  const workload = useGetCrmTaskWorkloadQuery(undefined, { skip: tab !== 'team' });

  const refresh = () => {
    if (listArgs) list.refetch();
    if (tab === 'team') workload.refetch();
    refetchCounts();
  };
  const onClaim = async (id) => {
    setError(null);
    try {
      await claim(id).unwrap();
    } catch (e) {
      setError(errText(e));
    }
    refresh();
  };

  if (isLoading) return <div style={{ textAlign: 'center', padding: '3rem' }}><Spinner animation="border" /></div>;
  if (isError || !lookups) return <div style={{ padding: '2rem' }}><Alert variant="warning">Tasks could not be loaded.</Alert></div>;

  const badge = { mine: counts?.mine, queues: counts?.queues, delegated: counts?.delegated };
  const tabs = lookups.is_admin ? [...TABS, ['settings', 'Settings', 'gear']] : TABS;

  return (
    <div style={{ background: work.canvas, minHeight: '100vh', padding: isMobile ? '0.75rem' : '1.5rem', color: work.ink }}>
      <div style={{
        background: work.surface, border: `1px solid ${work.hairline}`, borderTop: `3px solid ${work.deepBlue}`,
        borderRadius: 6, padding: isMobile ? '1rem' : '1.2rem 1.5rem', marginBottom: '1rem',
        display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap',
      }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <h3 style={{ fontWeight: 800, margin: 0, fontSize: isMobile ? '1.2rem' : '1.4rem' }}>Tasks</h3>
          <div style={{ color: work.muted, fontSize: '0.85rem' }}>
            {counts ? (
              <>
                {counts.mine} open for you
                {counts.mine_overdue > 0 && <span style={{ color: work.red, fontWeight: 700 }}> · {counts.mine_overdue} overdue</span>}
                {counts.mine_today > 0 && <span style={{ color: work.goldenrod, fontWeight: 700 }}> · {counts.mine_today} due today</span>}
                {counts.queues > 0 && <> · {counts.queues} waiting in your queues</>}
              </>
            ) : ' '}
          </div>
        </div>
        <button type="button" className="btn btn-sm" onClick={() => setShowNew(true)}
                style={{ background: work.deepBlue, color: 'white', fontWeight: 600, borderRadius: 4 }}>
          <i className="bi bi-plus-lg me-1" />New task
        </button>
      </div>

      <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap', borderBottom: `1px solid ${work.hairline}`, marginBottom: '0.75rem' }}>
        {tabs.map(([key, label, icon]) => (
          <button key={key} type="button" onClick={() => setTab(key)}
                  style={{
                    border: 0, background: 'none', padding: '0.5rem 0.8rem', fontWeight: 600, fontSize: '0.9rem',
                    color: tab === key ? work.deepBlue : work.muted,
                    borderBottom: `3px solid ${tab === key ? work.goldenrod : 'transparent'}`, marginBottom: -1,
                  }}>
            <i className={`bi bi-${icon} me-1`} />{label}
            {badge[key] > 0 && (
              <span style={{ marginLeft: 6, background: key === 'queues' ? work.goldenrod : 'rgba(10,41,71,0.1)', color: key === 'queues' ? 'white' : work.ink,
                             borderRadius: 10, padding: '0 7px', fontSize: '0.72rem' }}>{badge[key]}</span>
            )}
          </button>
        ))}
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)} style={{ fontSize: '0.85rem' }}>{error}</Alert>}

      {tab === 'settings' ? (
        <Settings lookups={lookups} />
      ) : (
        <>
          <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '0.75rem' }}>
            <Chip active={!category} onClick={() => setCategory('')}>All</Chip>
            {lookups.categories.map((c) => (
              <Chip key={c.key} active={category === c.key} onClick={() => setCategory(category === c.key ? '' : c.key)}>{c.label}</Chip>
            ))}
            <select className="form-select form-select-sm" value={status} onChange={(e) => setStatus(e.target.value)}
                    style={{ width: 'auto', marginLeft: 'auto' }}>
              <option value="open">Open</option>
              <option value="done">Done / cancelled</option>
              <option value="all">All</option>
            </select>
          </div>

          {tab === 'team' && (
            <Workload data={workload.data} isLoading={workload.isLoading} owner={teamOwner} queue={teamQueue}
                      onPick={(o, q) => { setTeamOwner(o); setTeamQueue(q); }} isMobile={isMobile} />
          )}

          <div style={{ background: work.surface, border: `1px solid ${work.hairline}`, borderRadius: 6, overflow: 'hidden' }}>
            {list.isLoading ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}><Spinner animation="border" size="sm" /></div>
            ) : list.isError ? (
              <div style={{ padding: '1.5rem', color: work.muted }}>Could not load tasks right now.</div>
            ) : (
              <TaskTable
                tasks={list.data?.tasks || []}
                lookups={lookups}
                onOpen={setOpenId}
                onClaim={tab === 'queues' || tab === 'team' ? onClaim : undefined}
                grouped={tab === 'mine' && status === 'open'}
                isMobile={isMobile}
                empty={EMPTY[tab](lookups)}
              />
            )}
          </div>
        </>
      )}

      <NewTaskModal show={showNew} onHide={() => setShowNew(false)} onCreated={refresh} />
      <TaskDrawer taskId={openId} onClose={() => setOpenId(null)} onChanged={refresh} />
    </div>
  );
}

const EMPTY = {
  mine: () => 'Nothing on your list. Tasks given to you, or taken from a queue, show up here.',
  queues: (l) => (l.my_queues.length
    ? 'Nothing waiting in your queues.'
    : 'You are not in any queue yet. A Work admin adds people to queues under Settings.'),
  delegated: () => 'Nothing handed off. Tasks you create for someone else or a queue show up here.',
  team: () => 'No open work matches.',
};

function Chip({ active, onClick, children }) {
  return (
    <button type="button" onClick={onClick}
            style={{
              border: `1px solid ${active ? work.deepBlue : work.hairline}`, background: active ? work.deepBlue : work.surface,
              color: active ? 'white' : work.ink, borderRadius: 14, padding: '0.15rem 0.7rem', fontSize: '0.8rem', fontWeight: 600,
            }}>
      {children}
    </button>
  );
}
Chip.propTypes = { active: PropTypes.bool, onClick: PropTypes.func.isRequired, children: PropTypes.node };

/** Open work per person and unclaimed per queue; clicking one filters the list. */
function Workload({ data, isLoading, owner, queue, onPick, isMobile }) {
  if (isLoading || !data) return null;
  const cell = { padding: '0.35rem 0.6rem', fontSize: '0.84rem' };
  const num = (n, color) => <span style={{ color: n ? color : work.muted, fontWeight: n ? 700 : 400 }}>{n}</span>;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
      <div style={{ background: work.surface, border: `1px solid ${work.hairline}`, borderRadius: 6, overflow: 'hidden' }}>
        <table className="table table-sm table-hover mb-0">
          <thead>
            <tr style={{ color: work.muted, fontSize: '0.7rem', textTransform: 'uppercase' }}>
              <th style={cell}>Person</th><th style={cell}>Open</th><th style={cell}>Overdue</th><th style={cell}>Next 7 days</th><th style={cell}>Waiting</th>
            </tr>
          </thead>
          <tbody>
            {data.owners.length === 0 && <tr><td colSpan={5} style={{ ...cell, color: work.muted }}>Nobody has open work yet.</td></tr>}
            {data.owners.map((o) => (
              <tr key={o.owner_uuid} onClick={() => onPick(owner === o.owner_uuid ? '' : o.owner_uuid, '')}
                  style={{ cursor: 'pointer', background: owner === o.owner_uuid ? 'rgba(208,155,44,0.12)' : undefined }}>
                <td style={{ ...cell, fontWeight: 600 }}>{o.owner_name}</td>
                <td style={cell}>{o.open}</td>
                <td style={cell}>{num(o.overdue, work.red)}</td>
                <td style={cell}>{num(o.due_soon, work.goldenrod)}</td>
                <td style={cell}>{o.waiting}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ background: work.surface, border: `1px solid ${work.hairline}`, borderRadius: 6, overflow: 'hidden' }}>
        <table className="table table-sm table-hover mb-0">
          <thead>
            <tr style={{ color: work.muted, fontSize: '0.7rem', textTransform: 'uppercase' }}>
              <th style={cell}>Unclaimed in queue</th><th style={cell}>Open</th><th style={cell}>Overdue</th>
            </tr>
          </thead>
          <tbody>
            {data.queues.length === 0 && <tr><td colSpan={3} style={{ ...cell, color: work.muted }}>Every queue is clear.</td></tr>}
            {data.queues.map((q) => (
              <tr key={q.queue_id} onClick={() => onPick('__none__', queue === String(q.queue_id) ? '' : String(q.queue_id))}
                  style={{ cursor: 'pointer', background: queue === String(q.queue_id) ? 'rgba(208,155,44,0.12)' : undefined }}>
                <td style={{ ...cell, fontWeight: 600 }}>{q.queue_name}</td>
                <td style={cell}>{q.open}</td>
                <td style={cell}>{num(q.overdue, work.red)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {(owner || queue) && (
        <div style={{ gridColumn: '1 / -1', fontSize: '0.82rem' }}>
          Filtered to one {queue ? 'queue' : 'person'}.{' '}
          <button type="button" className="btn btn-link btn-sm p-0" onClick={() => onPick('', '')}>Show everyone</button>
        </div>
      )}
    </div>
  );
}
Workload.propTypes = {
  data: PropTypes.object, isLoading: PropTypes.bool, owner: PropTypes.string, queue: PropTypes.string,
  onPick: PropTypes.func.isRequired, isMobile: PropTypes.bool,
};

/** WORK_ADMIN: queues with their members, and the task types per category. */
function Settings({ lookups }) {
  const { refetch } = useTaskLookups();
  const [saveQueue, { isLoading: savingQ }] = useSaveCrmTaskQueueMutation();
  const [saveType, { isLoading: savingT }] = useSaveCrmTaskTypeMutation();
  const [error, setError] = useState(null);
  const [newType, setNewType] = useState({ category: 'aum', name: '' });
  const [newQueue, setNewQueue] = useState('');

  const run = async (fn) => {
    setError(null);
    try {
      await fn();
      await refetch();
    } catch (e) {
      setError(errText(e));
    }
  };
  const queueBody = (q, patch) => ({ id: q.id, name: q.name, category: q.category, active: q.active, members: q.members, ...patch });
  const typeBody = (t, patch) => ({ id: t.id, category: t.category, name: t.name, default_queue_id: t.default_queue_id, active: t.active, ...patch });
  const card = { background: work.surface, border: `1px solid ${work.hairline}`, borderRadius: 6, padding: '0.9rem 1rem', marginBottom: '1rem' };
  const busy = savingQ || savingT;

  return (
    <div>
      {error && <Alert variant="danger" dismissible onClose={() => setError(null)} style={{ fontSize: '0.85rem' }}>{error}</Alert>}

      <div style={card}>
        <h6 style={{ fontWeight: 700 }}>Queues</h6>
        <p style={{ color: work.muted, fontSize: '0.82rem' }}>
          One per product line, company-wide. Members see the queue&apos;s unclaimed work and can take it,
          whatever office the client is in.
        </p>
        {lookups.queues.map((q) => (
          <div key={q.id} style={{ borderTop: `1px solid ${work.hairline}`, padding: '0.6rem 0', opacity: q.active ? 1 : 0.55 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: 6 }}>
              <strong style={{ flex: 1 }}>{q.name}</strong>
              <span style={{ fontSize: '0.75rem', color: work.muted }}>{lookups.categoryLabel[q.category] || 'Any category'}</span>
              <button type="button" className="btn btn-link btn-sm p-0" disabled={busy}
                      onClick={() => run(() => saveQueue(queueBody(q, { active: !q.active })).unwrap())}>
                {q.active ? 'Turn off' : 'Turn on'}
              </button>
            </div>
            <MemberEditor people={lookups.people} members={q.members} disabled={busy}
                          onChange={(members) => run(() => saveQueue(queueBody(q, { members })).unwrap())} />
          </div>
        ))}
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.6rem' }}>
          <input className="form-control form-control-sm" placeholder="New queue name" value={newQueue} maxLength={80}
                 onChange={(e) => setNewQueue(e.target.value)} />
          <button type="button" className="btn btn-sm btn-outline-primary" disabled={busy || !newQueue.trim()}
                  onClick={() => run(async () => { await saveQueue({ name: newQueue.trim(), active: true, members: [] }).unwrap(); setNewQueue(''); })}>
            Add queue
          </button>
        </div>
      </div>

      <div style={card}>
        <h6 style={{ fontWeight: 700 }}>Task types</h6>
        <p style={{ color: work.muted, fontSize: '0.82rem' }}>
          A task&apos;s type sets its category and suggests its queue. Turning a type off hides it from new tasks;
          tasks that already have it keep it.
        </p>
        {lookups.categories.map((c) => {
          const list = lookups.types.filter((t) => t.category === c.key);
          return (
            <div key={c.key} style={{ borderTop: `1px solid ${work.hairline}`, padding: '0.5rem 0' }}>
              <div style={{ fontWeight: 700, fontSize: '0.85rem', marginBottom: 4 }}>{c.label}</div>
              {list.map((t) => (
                <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.15rem 0', opacity: t.active ? 1 : 0.5, flexWrap: 'wrap' }}>
                  <span style={{ flex: 1, minWidth: 160, fontSize: '0.85rem' }}>{t.name}</span>
                  <select className="form-select form-select-sm" style={{ width: 'auto' }} value={t.default_queue_id || ''} disabled={busy}
                          onChange={(e) => run(() => saveType(typeBody(t, { default_queue_id: e.target.value ? Number(e.target.value) : null })).unwrap())}>
                    <option value="">No default queue</option>
                    {lookups.queues.map((q) => <option key={q.id} value={q.id}>{q.name}</option>)}
                  </select>
                  <button type="button" className="btn btn-link btn-sm p-0" disabled={busy}
                          onClick={() => run(() => saveType(typeBody(t, { active: !t.active })).unwrap())}>
                    {t.active ? 'Turn off' : 'Turn on'}
                  </button>
                </div>
              ))}
            </div>
          );
        })}
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.6rem', flexWrap: 'wrap' }}>
          <select className="form-select form-select-sm" style={{ width: 'auto' }} value={newType.category}
                  onChange={(e) => setNewType({ ...newType, category: e.target.value })}>
            {lookups.categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
          <input className="form-control form-control-sm" style={{ flex: 1, minWidth: 180 }} placeholder="New type name" maxLength={120}
                 value={newType.name} onChange={(e) => setNewType({ ...newType, name: e.target.value })} />
          <button type="button" className="btn btn-sm btn-outline-primary" disabled={busy || !newType.name.trim()}
                  onClick={() => run(async () => {
                    const q = lookups.queues.find((x) => x.category === newType.category && x.active);
                    await saveType({ category: newType.category, name: newType.name.trim(), default_queue_id: q?.id || null, active: true }).unwrap();
                    setNewType({ ...newType, name: '' });
                  })}>
            Add type
          </button>
        </div>
      </div>
    </div>
  );
}
Settings.propTypes = { lookups: PropTypes.object.isRequired };

function MemberEditor({ people, members, onChange, disabled }) {
  const name = Object.fromEntries(people.map((p) => [p.uuid, p.name]));
  const options = people.filter((p) => !members.includes(p.uuid));
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', alignItems: 'center' }}>
      {members.length === 0 && <span style={{ fontSize: '0.8rem', color: work.muted }}>No members yet.</span>}
      {members.map((u) => (
        <span key={u} style={{ background: 'rgba(10,41,71,0.07)', borderRadius: 4, padding: '0.1rem 0.4rem', fontSize: '0.8rem' }}>
          {name[u] || 'No longer has Tasks access'}
          <button type="button" aria-label="Remove" disabled={disabled} onClick={() => onChange(members.filter((x) => x !== u))}
                  style={{ border: 0, background: 'none', padding: '0 0 0 4px', color: work.muted }}>
            <i className="bi bi-x" />
          </button>
        </span>
      ))}
      <select className="form-select form-select-sm" style={{ width: 'auto' }} value="" disabled={disabled}
              onChange={(e) => e.target.value && onChange([...members, e.target.value])}>
        <option value="">Add member...</option>
        {options.map((p) => <option key={p.uuid} value={p.uuid}>{p.name}</option>)}
      </select>
    </div>
  );
}
MemberEditor.propTypes = {
  people: PropTypes.arrayOf(PropTypes.object).isRequired,
  members: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};
